import shopifyFetch from "./APIFetch.js";

export const loginCustomer = async (email, password) => {
    const mutation = `
        mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
            customerAccessTokenCreate(input: $input) {
                customerAccessToken {
                    accessToken
                    expiresAt
                }
                userErrors {
                    field
                    message
                }
            }
        }
    `;

    const variables = {
        input: {
            email,
            password,
        },
    };

    try {
        const data = await shopifyFetch(mutation, variables);
        const result = data.customerAccessTokenCreate;

        if (result.userErrors.length > 0) {
            return {success: false, error: result.userErrors[0].message};
        }

        return {
            success: true,
            accessToken: result.customerAccessToken.accessToken,
            expiresAt: result.customerAccessToken.expiresAt,
        };
    } catch (err) {
        console.error("Login failed:", err);
        return {success: false, error: "Wystąpił błąd serwera. Spróbuj później."};
    }
};

export const registerCustomer = async (email, password) => {
    const mutation = `
        mutation customerCreate($input: CustomerCreateInput!) {
            customerCreate(input: $input) {
                customer {
                    id
                    email
                }
                customerUserErrors {
                    code
                }
            }
        }
    `;

    const variables = {
        input: {
            email,
            password,
        },
    };

    try {
        const data = await shopifyFetch(mutation, variables);
        const result = data.customerCreate;

        if (result.customerUserErrors.length > 0) {
            return {success: false, errorCode: result.customerUserErrors[0].code};
        }

        return {success: true};
    } catch (err) {
        console.error("Registration failed:", err);
        return {success: false, errorCode: "SERVER_ERROR"};
    }
};

export const getCustomerInfo = async (accessToken) => {
    const query = `
    query getCustomerInfo($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        firstName
        lastName
        email
        phone
        acceptsMarketing
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken
    };

    try {
        const res = await shopifyFetch(query, variables);

        return res.customer || null;
    } catch (err) {
        console.error("Failed to fetch customer info:", err);
        return null;
    }
};

export const updateCustomerInfo = async (accessToken, {firstName, lastName, phone, acceptsMarketing}) => {
    const mutation = `
    mutation updateCustomerInfo($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
      customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
        customer {
          firstName
          lastName
          phone
          acceptsMarketing
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken,
        customer: {
            firstName,
            lastName,
            phone,
            acceptsMarketing
        }
    };

    try {
        const res = await shopifyFetch(mutation, variables);
        const errors = res.customerUpdate?.customerUserErrors;

        if (errors && errors.length > 0) {
            console.error("Shopify returned errors:", errors);
            return {success: false, errors};
        }

        return {success: true, customer: res.customerUpdate.customer};
    } catch (err) {
        console.error("Failed to update customer info:", err);
        return {success: false, errors: [err.message]};
    }
};

export const createDeliveryAddress = async (accessToken, addressObj) => {
    const [firstName, ...lastNameParts] = addressObj.name.trim().split(" ");
    const lastName = lastNameParts.join(" ") || "-";

    const mutation = `
    mutation CreateCustomerAddress($customerAccessToken: String!, $address: MailingAddressInput!) {
      customerAddressCreate(customerAccessToken: $customerAccessToken, address: $address) {
        customerAddress {
          id
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken,
        address: {
            firstName,
            lastName,
            address1: addressObj.street,
            zip: addressObj.postal,
            city: addressObj.city,
            phone: addressObj.phone,
            country: "Poland"
        }
    };

    try {
        const res = await shopifyFetch(mutation, variables);
        const errors = res.customerAddressCreate?.customerUserErrors;

        if (errors?.length > 0) {
            console.error("Create address errors:", errors);
            return {success: false, errors};
        }

        return {
            success: true,
            id: res.customerAddressCreate.customerAddress.id
        };
    } catch (err) {
        console.error("Failed to create address:", err);
        return {success: false, errors: [err.message]};
    }
};

export const editDeliveryAddress = async (accessToken, addressId, addressObj) => {
    const [firstName, ...lastNameParts] = addressObj.name.trim().split(" ");
    const lastName = lastNameParts.join(" ") || "-";

    const mutation = `
    mutation EditCustomerAddress($customerAccessToken: String!, $id: ID!, $address: MailingAddressInput!) {
      customerAddressUpdate(customerAccessToken: $customerAccessToken, id: $id, address: $address) {
        customerAddress {
          id
        }
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken,
        id: addressId,
        address: {
            firstName,
            lastName,
            address1: addressObj.street,
            zip: addressObj.postal,
            city: addressObj.city,
            phone: addressObj.phone,
            country: "Poland"
        }
    };

    try {
        const res = await shopifyFetch(mutation, variables);
        const errors = res.customerAddressUpdate?.customerUserErrors;

        if (errors?.length > 0) {
            console.error("Edit address errors:", errors);
            return {success: false, errors};
        }

        return {
            success: true,
            id: res.customerAddressUpdate.customerAddress.id
        };
    } catch (err) {
        console.error("Failed to edit address:", err);
        return {success: false, errors: [err.message]};
    }
};

export const getDeliveryAddresses = async (accessToken) => {
    const query = `
    query GetCustomerAddresses($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        addresses(first: 10) {
          edges {
            node {
              id
              firstName
              lastName
              address1
              zip
              city
              phone
            }
          }
        }
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken
    };

    try {
        const res = await shopifyFetch(query, variables);
        const rawAddresses = res.customer?.addresses?.edges || [];

        const parsedAddresses = rawAddresses.map(({node}) => ({
            id: node.id,
            name: `${node.firstName} ${node.lastName}`.trim(),
            street: node.address1,
            postal: `${node.zip}`,
            city: ` ${node.city}`,
            phone: node.phone || ""
        }));

        return parsedAddresses;
    } catch (err) {
        console.error("Failed to fetch delivery addresses:", err);
        return [];
    }
};

export const deleteDeliveryAddress = async (accessToken, addressId) => {
    const mutation = `
    mutation DeleteCustomerAddress($customerAccessToken: String!, $id: ID!) {
      customerAddressDelete(customerAccessToken: $customerAccessToken, id: $id) {
        deletedCustomerAddressId
        customerUserErrors {
          field
          message
        }
      }
    }
  `;

    const variables = {
        customerAccessToken: accessToken,
        id: addressId
    };

    try {
        const res = await shopifyFetch(mutation, variables);
        const errors = res.customerAddressDelete?.customerUserErrors;

        if (errors?.length > 0) {
            console.error("Delete address errors:", errors);
            return {success: false, errors};
        }

        return {
            success: true,
            deletedId: res.customerAddressDelete?.deletedCustomerAddressId
        };
    } catch (err) {
        console.error("Failed to delete address:", err);
        return {success: false, errors: [err.message]};
    }
};

// CustomerHandler.js
export const fetchUserOrdersBrief = async (
    accessToken,
    {sortKey = "PROCESSED_AT", reverse = true} = {}
) => {
    const query = `
        query GetCustomerOrders(
          $customerAccessToken: String!,
          $sortKey: OrderSortKeys!,
          $reverse: Boolean!
        ) {
            customer(customerAccessToken: $customerAccessToken) {
                orders(first: 10, sortKey: $sortKey, reverse: $reverse) {
                    edges {
                        node {
                            id
                            name
                            processedAt
                            financialStatus
                            totalPrice { amount currencyCode }
                            lineItems(first: 10) {
                                edges {
                                    node {
                                        variant {
                                            product {
                                                images(first: 1) {
                                                    edges { 
                                                      node { 
                                                        url 
                                                        altText 
                                                      } 
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    `;

    const variables = {
        customerAccessToken: accessToken,
        sortKey,
        reverse
    };

    try {
        const res = await shopifyFetch(query, variables);
        const ordersRaw = res?.customer?.orders?.edges ?? [];

        return ordersRaw.map(({node}) => {
            const dateObj = new Date(node.processedAt);
            const formattedDate = dateObj
                .toLocaleDateString("pl-PL", {day: "2-digit", month: "2-digit", year: "numeric"})
                .replace(/\./g, "-")
                .replace(/\s/g, "");

            const formattedPrice =
                `${Number(node.totalPrice.amount).toFixed(2)} ${node.totalPrice.currencyCode.toLowerCase()}`
                    .split(" ")[0];

            return {
                status: node.financialStatus,
                date: formattedDate,
                orderID: node.name,
                totalPrice: formattedPrice,
                items: node.lineItems.edges
                    .map(({node}) => {
                        const img = node.variant?.product?.images?.edges?.[0]?.node ?? null;
                        if (!img?.url) return null;
                        return {
                            url: img.url,
                            alt: img.altText ?? node.name // fallback do orderID
                        };
                    })
                    .filter(Boolean)
            };
        });
    } catch (err) {
        console.error("Failed to fetch user orders:", err);
        return [];
    }
};


/* ============================
   UPDATED: fetchUserOrderDetails
   ============================ */

function formatDateDDMMYYYY(iso) {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

function toNumber2(n) {
    const num = Number(n);
    if (Number.isNaN(num)) return null;
    return Number(num.toFixed(2));
}

/**
 * Fetch one order by ID or name (Storefront API).
 * @param {string} orderIdOrName - order GID or order name like "#1004"
 * @param {string} customerAccessToken
 * @param {number} maxPages
 */
export const fetchUserOrderDetails = async (orderIdOrName, customerAccessToken, maxPages = 20) => {
    const query = `
    query CustomerOrders($customerAccessToken: String!, $first: Int!, $after: String) {
      customer(customerAccessToken: $customerAccessToken) {
        id
        orders(first: $first, reverse: true, after: $after) {
          pageInfo { hasNextPage endCursor }
          nodes {
            id
            name
            processedAt

            fulfillmentStatus

            billingAddress {
              name
              address1
              address2
              city
              zip
              country
              phone
            }

            shippingAddress {
              name
              address1
              address2
              city
              zip
              country
              phone
            }

            currentTotalShippingPrice { amount currencyCode }
            totalShippingPrice { amount currencyCode }

            lineItems(first: 100) {
              edges {
                node {
                  title
                  quantity
                  originalTotalPrice { amount currencyCode }
                  discountedTotalPrice { amount currencyCode }
                  variant { 
                    image { 
                      url
                      altText
                    } 
                  }
                }
              }
            }

            successfulFulfillments {
              trackingCompany
              trackingInfo { number url }
            }

            invoiceVat: metafield(namespace: "invoice", key: "vat_number") { value }
            shippingVat: metafield(namespace: "shipping", key: "vat_number") { value }
          }
        }
      }
    }
  `;

    const normalizeKey = v => String(v ?? "").trim();
    const want = normalizeKey(orderIdOrName);
    const wantNoHash = want.replace(/^#/, "");

    let after = null;
    let page = 0;

    while (page < maxPages) {
        const variables = {customerAccessToken, first: 50, after};
        const data = await shopifyFetch(query, variables);

        const customer = data?.customer;
        if (!customer) throw new Error("Unauthorized: invalid customer access token.");

        const orders = customer.orders?.nodes ?? [];
        const found = orders.find(o => {
            const id = normalizeKey(o.id);
            const name = normalizeKey(o.name);
            const nameNoHash = name.replace(/^#/, "");
            return id === want || name === want || nameNoHash === wantNoHash;
        });

        if (found) {
            const billing = found.billingAddress || {};
            const shipping = found.shippingAddress || {};
            const billingAddressStr = [billing.address1, billing.address2].filter(Boolean).join(", ");
            const shippingAddressStr = [shipping.address1, shipping.address2].filter(Boolean).join(", ");

            const items = (found.lineItems?.edges ?? []).map(({node}) => {
                const qty = node.quantity || 1;
                const unitDiscounted = node.discountedTotalPrice ? Number(node.discountedTotalPrice.amount) / qty : null;
                const unitOriginal = node.originalTotalPrice ? Number(node.originalTotalPrice.amount) / qty : null;
                const unit = unitDiscounted ?? unitOriginal;
                return {
                    image: node.variant?.image?.url ?? null,
                    alt: node.variant?.image?.altText ?? null,
                    name: node.title,
                    amount: qty,
                    price: unit == null ? null : toNumber2(unit) // float, precision 2
                };
            });

            const shippingMoney =
                found.currentTotalShippingPrice ?? found.totalShippingPrice ?? null;

            const deliveryPrice =
                shippingMoney?.amount != null ? Number(shippingMoney.amount).toFixed(2) : null;

            const tracking = (found.successfulFulfillments ?? [])
                .flatMap(f => (f?.trackingInfo ?? []).map(ti => ({
                    company: f?.trackingCompany || null,
                    number: ti?.number || null,
                    url: ti?.url || null
                })))
                .filter((v, i, arr) =>
                    i === arr.findIndex(x => x.number === v.number && x.url === v.url)
                );

            return {
                id: found.name,
                date: formatDateDDMMYYYY(found.processedAt),
                fulfillmentStatus: found.fulfillmentStatus,

                invoiceData: found.billingAddress ? {
                    name: billing.name || "",
                    nip: found.invoiceVat?.value ?? null,
                    address: billingAddressStr,
                    postal: billing.zip || "",
                    city: billing.city || "",
                    country: billing.country || "",
                    phoneNumber: billing.phone || ""
                } : null,

                shippingAddress: found.shippingAddress ? {
                    name: shipping.name || "",
                    nip: found.shippingVat?.value ?? null,
                    address: shippingAddressStr,
                    postal: shipping.zip || "",
                    city: shipping.city || "",
                    country: shipping.country || "",
                    phoneNumber: shipping.phone || ""
                } : null,

                items,
                deliveryPrice,
                tracking
            };
        }

        const pageInfo = customer.orders?.pageInfo;
        if (!pageInfo?.hasNextPage) break;
        after = pageInfo.endCursor || null;
        page += 1;
    }

    throw new Error(
        "Order not found for this customer. Ensure the order belongs to the logged-in account and pass either the full GID or the order name (e.g., '#1004')."
    );
};


export async function getUserFirstName(userAccessToken) {
    if (!userAccessToken) throw new Error("Missing userAccessToken");

    const query = `
      query GetCustomerFirstName($customerAccessToken: String!) {
        customer(customerAccessToken: $customerAccessToken) {
          firstName
        }
      }
    `;

    const variables = {customerAccessToken: userAccessToken};

    const res = await shopifyFetch(query, variables);


    return res?.customer?.firstName || null;
}


export const resetByEmail = async (email) => {
    const mutation = `
        mutation customerRecover($email: String!) {
            customerRecover(email: $email) {
                customerUserErrors {
                    field
                    message
                }
            }
        }
    `;

    const variables = { email };

    try {
        const data = await shopifyFetch(mutation, variables);
        const result = data.customerRecover;

        if (result.customerUserErrors.length > 0) {
            return { success: false, error: result.customerUserErrors[0].message };
        }

        return { success: true };
    } catch (err) {
        console.error("Password reset failed:", err);
        return { success: false, error: "Wystąpił błąd serwera. Spróbuj później." };
    }
};